'use client';

import { useState } from 'react';
import { Accordion } from 'rizzui';

export type LandingFaqItem = {
  question: string;
  answer: string;
};

type LandingFaqProps = {
  items: readonly LandingFaqItem[];
  variant?: 'home' | 'editorial';
  className?: string;
};

export function LandingFaq({
  items,
  variant = 'editorial',
  className = '',
}: LandingFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (variant === 'home') {
    return (
      <div className={className}>
        {items.map((item, index) => (
          /*
           * Native <details>, matching the source design and the documented exception to
           * the RizzUI rule in the root AGENTS.md. The template styles the open state via
           * `.sec-8-home-12__item[open]`, an attribute selector only a real details
           * element can satisfy, a RizzUI Accordion renders a <div>, so the toggle bars
           * never resolved into the minus glyph. The shared `name` also gives the group
           * single-open behaviour natively, with no state to keep in sync.
           */
          <details
            key={item.question}
            className="sec-8-home-12__item at_fade_anim"
            data-fade-from="bottom"
            data-delay={`.${(index + 1) * 5}`}
            name="ShipSmart-home-12-faq"
            open={index === 0}
          >
            <summary className="sec-8-home-12__summary">
              <span className="sec-8-home-12__question">{item.question}</span>
              <span className="sec-8-home-12__toggle" aria-hidden="true">
                <span className="sec-8-home-12__bar sec-8-home-12__bar--h" />
                <span className="sec-8-home-12__bar sec-8-home-12__bar--v" />
              </span>
            </summary>
            <div className="sec-8-home-12__answer">
              <span className="sec-8-home-12__divider" aria-hidden="true" />
              <p className="mb-0">{item.answer}</p>
            </div>
          </details>
        ))}
      </div>
    );
  }

  return (
    <div className={`mx-auto mt-12 grid max-w-[800px] gap-3 ${className}`}>
      {items.map((item, index) => {
        const open = openIndex === index;

        return (
          <Accordion
            className="overflow-hidden rounded-[17px] border border-black/15 bg-white"
            defaultOpen={open}
            duration={260}
            key={`${item.question}-${open ? 'open' : 'closed'}`}
          >
            <Accordion.Header
              className="flex min-h-[76px] w-full items-center justify-between gap-5 px-7 text-left text-[16px] font-semibold text-[#17181b]"
              onClick={() =>
                setOpenIndex((current) => (current === index ? null : index))
              }
            >
              {({ open: accordionOpen }) => (
                <>
                  <span>{item.question}</span>
                  <span
                    aria-hidden="true"
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-[25px] leading-none ${accordionOpen ? 'bg-[#111214] text-white' : 'bg-[#f3f3f1] text-[#17181b]'}`}
                  >
                    {accordionOpen ? '−' : '+'}
                  </span>
                </>
              )}
            </Accordion.Header>
            <Accordion.Body className="px-7 pb-7">
              <div className="border-t border-black/10 pt-5 text-[16px] leading-[1.55] text-[#4f5156]">
                {item.answer}
              </div>
            </Accordion.Body>
          </Accordion>
        );
      })}
    </div>
  );
}
